import React from 'react';
import { Info } from 'lucide-react';

/**
 * feat-053b estimated-marker — the net-new design over the (faithful) export.
 * Driven entirely by the payload `placeholder`/`data_source` flag (ADR D3); the
 * UI never decides what's seeded. Slate token (`preview-*`, APPROVED option ii)
 * keeps it distinct from the env-caution amber so the two cautions never blur.
 *
 * Three placements (estimated-marker-mockup.html §A):
 *  - `chip`  — full inline chip beside a seeded delta/value (one chip per row).
 *  - `dot`   — compact dot for dense tables (per-store delta cells).
 *  - `panel` — panel-header chip covering a whole seeded panel (custom label).
 *
 * Tooltip copy is fixed: "Seeded preview — not yet measured from your data."
 */
const TOOLTIP = 'Seeded preview — not yet measured from your data.';

type Variant = 'chip' | 'dot' | 'panel';

interface EstimatedMarkerProps {
  variant?: Variant;
  /** Panel-header label, e.g. "Sample — estimated" / "Some comparisons estimated". */
  label?: string;
  className?: string;
}

const EstimatedMarker: React.FC<EstimatedMarkerProps> = ({
  variant = 'chip',
  label,
  className = '',
}) => {
  if (variant === 'dot') {
    return (
      <span
        className={`inline-block w-1.5 h-1.5 rounded-full align-middle bg-preview ${className}`}
        title={TOOLTIP}
        aria-label="Estimated"
        role="img"
      />
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-preview-light text-preview ${className}`}
      title={TOOLTIP}
    >
      <Info className="w-3 h-3" strokeWidth={2.2} />
      {label ?? 'Estimated'}
    </span>
  );
};

export default EstimatedMarker;
